import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { ApiService } from 'services/api.service';
import { TooltipListItem, UpdateTooltipRequest } from 'types/tooltip.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseFormComponent } from 'components/form/base-form';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastService } from 'services/toast.service';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import { NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { FILE_OBJECT_TYPE_TOOLTIP, FileCard } from 'types/file.interface';

@Component({
  selector: 'app-edit-tooltip-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    TranslateModule,
    ReactiveFormsModule,
    NgClass,
    TooltipComponent,
    SpinnerComponent,
    ConfirmModalComponent,
    FileUploadComponent
  ],
  templateUrl: './edit-tooltip-page.component.html',
  styleUrls: ['./edit-tooltip-page.component.scss', '../../../components/form/base-form.scss']
})
export class EditTooltipPageComponent extends BaseFormComponent implements OnInit {
  tooltip: TooltipListItem | null = null;
  id: string = '';
  isLoading: boolean = true;
  isSubmitting: boolean = false;
  isSubmittingFiles: boolean = false;
  isImageLoading: boolean = true;
  imageUrl?: string;
  isOpenConfirmDeleteImage: boolean = false;

  public readonly maxLengthText = MAX_LENGTH_ADDITIONAL_INFO;
  public readonly maxLengthTextEn = MAX_LENGTH_ADDITIONAL_INFO;
  public readonly maxLengthDescription = MAX_LENGTH_ADDITIONAL_INFO;
  public readonly tooltipFileType = FILE_OBJECT_TYPE_TOOLTIP;

  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.admin',
      url: '/'
    },
    {
      title: 'components.breadcrumbs.tooltips',
      url: '/uznirstosie-portala-padomi'
    },
    {
      title: 'components.breadcrumbs.editTooltip',
      url: '/uznirstosie-portala-padomi/rediget/{id}'
    }
  ];

  get text(): AbstractControl<any, any> | null {
    return this.form?.get('text');
  }

  get textEn(): AbstractControl<any, any> | null {
    return this.form?.get('textEn');
  }

  get description(): AbstractControl<any, any> | null {
    return this.form?.get('description');
  }

  constructor(
    protected override apiService: ApiService,
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override translateService: TranslateService,
    protected override toastService: ToastService,
    protected route: ActivatedRoute
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
    this.route.params.subscribe((params) => {
      this.id = params['id'];
    });
    this.fileUploadComponentCount = 1;
  }

  override ngOnInit(): void {
    this.isLoading = true;
    this.apiService.getTooltip(this.id).subscribe((tooltip) => {
      this.tooltip = tooltip;

      if (this.tooltip?.file) {
        this.displayImage(this.tooltip.file);
      } else {
        this.isImageLoading = false;
        this.isLoading = false;
      }
      this.fillForm();
    });
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      text: [this.tooltip?.text || '', [Validators.required, Validators.maxLength(this.maxLengthText)]],
      textEn: [this.tooltip?.textEn || '', [Validators.required, Validators.maxLength(this.maxLengthTextEn)]],
      description: [this.tooltip?.description || '', [Validators.required, Validators.maxLength(this.maxLengthDescription)]]
    });
  }

  override submit(): void {
    this.submitAttempted = true;

    if (this.form.valid) {
      this.isSubmitting = true;
      const args: UpdateTooltipRequest = {
        id: this.id,
        code: this.tooltip?.code || '',
        text: this.text?.value,
        textEn: this.textEn?.value,
        description: this.description?.value
      };

      this.apiService.updateTooltip(this.id, args).subscribe(
        () => {
          if (this.tooltip?.file) {
            this.onSubmitSuccess();
          } else {
            this.isSubmittingFiles = true;
          }
        },
        (error) => {
          this.isLoading = false;
          console.error(error);
          this.toastService.showRequestError();
        }
      );
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form));
    }
  }

  override onSubmitSuccess(): void {
    this.isSubmittingFiles = false;
    this.uploadComponentsReceived = 0;
    this.fileActions = [];
    this.isLoading = false;
    this.toastService.showSuccess('updateTooltip', '', { shouldTranslate: true });

    setTimeout(() => {
      window.location.reload();
    }, 200);
  }

  displayImage(file: FileCard): void {
    this.apiService.downloadFile(file.outerId!).subscribe((data) => {
      const blob = new Blob([data], { type: 'image/jpeg' });
      this.imageUrl = window.URL.createObjectURL(blob);
      this.isImageLoading = false;
      this.isLoading = false;
    });
  }

  deleteImage(): void {
    this.apiService.deleteFile(this.tooltip?.file?.outerId || '').subscribe(() => {
      this.imageUrl = undefined;
    });
  }
}
